import React, { useState, useEffect, useRef } from 'react';
import { DataTable, DataTableStateEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Product } from '../types/product';
import * as apiService from '../api';
import { Toast } from 'primereact/toast';

interface LazyState {
  first: number;
  rows: number;
  page: number;
  search: string;
  sortField: string;
  sortOrder: 'asc' | 'desc' | null | undefined;
}

type ProductDataType = {
  id?: number;
  name: string;
  description: string;
  basePrice: number;
  isActive: boolean;
  category?: string;
  manufacturer?: string;
  attributes?: { [key: string]: string };
  imageUrl?: string;
  quickbooksItemId?: string;
};

export const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lazyState, setLazyState] = useState<LazyState>({
    first: 0,
    rows: 10,
    page: 1,
    search: '',
    sortField: 'createdAt',
    sortOrder: 'desc'
  });
  const toast = useRef<Toast>(null);
  const [formData, setFormData] = useState<ProductDataType>({
    name: '',
    description: '',
    basePrice: 0,
    isActive: true,
    category: '',
    manufacturer: '',
    attributes: {},
    imageUrl: '',
    quickbooksItemId: '',
  });

  useEffect(() => {
    loadProducts();
  }, [lazyState]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProducts({
        page: lazyState.page,
        limit: lazyState.rows,
        search: lazyState.search,
        sortBy: lazyState.sortField,
        sortOrder: lazyState.sortOrder || 'desc'
      });
      if (response && Array.isArray(response.data)) {
        setProducts(response.data);
        setTotalRecords(response.totalCount);
      } else {
        console.error('Invalid response format:', response);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Invalid data format received from server',
          life: 3000
        });
      }
    } catch (error) {
      console.error('Error loading products:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load products',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const onPage = (event: DataTableStateEvent) => {
    setLazyState(prev => ({
      ...prev,
      first: event.first || 0,
      rows: event.rows || 10,
      page: Math.floor((event.first || 0) / (event.rows || 10)) + 1
    }));
  };

  const onSort = (event: DataTableStateEvent) => {
    setLazyState(prev => ({
      ...prev,
      sortField: event.sortField || 'createdAt',
      sortOrder: event.sortOrder === 1 ? 'asc' : event.sortOrder === -1 ? 'desc' : null
    }));
  };

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value;
    setLazyState(prev => ({
      ...prev,
      search: searchValue,
      page: 1,
      first: 0
    }));
  };

  const openNew = () => {
    setFormData({
      name: '',
      description: '',
      basePrice: 0,
      isActive: true,
      category: '',
      manufacturer: '',
      attributes: {},
      imageUrl: '',
      quickbooksItemId: '',
    });
    setEditMode(false);
    setDialogVisible(true);
  };

  const editProduct = (product: Product) => {
    setFormData({
      id: product.id,
      name: product.name,
      description: product.description,
      basePrice: product.basePrice,
      isActive: product.isActive,
      category: product.category || '',
      manufacturer: product.manufacturer || '',
      attributes: product.attributes || {},
      imageUrl: product.imageUrl || '',
      quickbooksItemId: product.quickbooksItemId || '',
    });
    setEditMode(true);
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
  };

  const [syncLoading, setSyncLoading] = useState(false);

  const syncWithQuickbooks = async (productId: number) => {
    try {
      setSyncLoading(true);
      const result = await apiService.syncSingleProductWithQuickbooks(productId);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: result.message || 'Product synced with QuickBooks successfully',
        life: 3000
      });
      loadProducts(); // Reload to get updated QuickBooks ID if it was a new product
    } catch (error) {
      console.error('Error syncing with QuickBooks:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to sync with QuickBooks',
        life: 3000
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const saveProduct = async () => {
    try {
      // Destructure id and create data object without id field
      const { id, ...formDataWithoutId } = formData;
      
      const productData = {
        ...formDataWithoutId,
        basePrice: Number(formData.basePrice),
        category: formData.category?.trim() || undefined,
        manufacturer: formData.manufacturer?.trim() || undefined,
        imageUrl: formData.imageUrl?.trim() || undefined,
        quickbooksItemId: formData.quickbooksItemId?.trim() || undefined,
        attributes: Object.keys(formData.attributes || {}).length > 0 ? formData.attributes : undefined,
      };

      // Remove undefined values
      const cleanedData = Object.fromEntries(
        Object.entries(productData).filter(([key, value]) => value !== undefined)
      ) as Omit<ProductDataType, 'id'>;

      let savedProductId: number;

      if (editMode) {
        const updatedProduct = await apiService.updateProduct(formData.id!, cleanedData);
        savedProductId = updatedProduct.id;
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Product updated successfully',
          life: 3000
        });
      } else {
        const newProduct = await apiService.createProduct(cleanedData);
        savedProductId = newProduct.id;
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Product created successfully',
          life: 3000
        });
      }
      
      // Ask if user wants to sync with QuickBooks using PrimeReact's confirmDialog
      confirmDialog({
        message: 'Would you like to sync this product with QuickBooks?',
        header: 'Sync with QuickBooks',
        icon: 'pi pi-question-circle',
        accept: async () => {
          await syncWithQuickbooks(savedProductId);
        }
      });
      
      hideDialog();
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save product',
        life: 3000
      });
    }
  };

  const deleteProduct = async (product: Product) => {
    try {
      await apiService.deleteProduct(product.id);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Product deleted successfully',
        life: 3000
      });
      loadProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete product',
        life: 3000
      });
    }
  };

  const priceBodyTemplate = (rowData: Product) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(rowData.basePrice);
  };

  const actionBodyTemplate = (rowData: Product) => {
    return (
      <div className="flex gap-2">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-success" onClick={() => editProduct(rowData)} />
        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => deleteProduct(rowData)} />
        {rowData.quickbooksItemId && (
          <Button 
            icon="pi pi-sync" 
            className="p-button-rounded p-button-info" 
            onClick={() => syncWithQuickbooks(rowData.id)} 
            tooltip="Sync with QuickBooks"
          />
        )}
      </div>
    );
  };

  const dialogFooter = (
    <div>
      <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
      <Button label="Save" icon="pi pi-check" onClick={saveProduct} />
    </div>
  );

  const header = (
    <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
      <h4 className="m-0">Products</h4>
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          type="search"
          placeholder="Search..."
          value={lazyState.search}
          onChange={onSearch}
        />
      </span>
      <Button label="New Product" icon="pi pi-plus" onClick={openNew} />
    </div>
  );

  return (
    <div>
      <Toast ref={toast} />
      <ConfirmDialog />

      <DataTable
        value={products}
        lazy
        dataKey="id"
        paginator
        first={lazyState.first}
        rows={lazyState.rows}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={lazyState.sortField}
        sortOrder={lazyState.sortOrder === 'asc' ? 1 : lazyState.sortOrder === 'desc' ? -1 : undefined}
        loading={loading}
        header={header}
        responsiveLayout="scroll"
        emptyMessage="No products found"
      >
        <Column field="name" header="Name" sortable />
        <Column field="description" header="Description" sortable />
        <Column field="basePrice" header="Price" body={priceBodyTemplate} sortable />
        <Column field="category" header="Category" sortable />
        <Column field="manufacturer" header="Manufacturer" sortable />
        <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '8rem' }} />
      </DataTable>

      <Dialog 
        visible={dialogVisible} 
        style={{ width: '450px' }} 
        header={editMode ? "Edit Product" : "New Product"} 
        modal 
        className="p-fluid" 
        footer={dialogFooter} 
        onHide={hideDialog}
      >
        <div className="field">
          <label htmlFor="name">Name</label>
          <InputText 
            id="name" 
            value={formData.name} 
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} 
            required 
          />
        </div>

        <div className="field">
          <label htmlFor="description">Description</label>
          <InputText 
            id="description" 
            value={formData.description} 
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} 
            required 
          />
        </div>

        <div className="field">
          <label htmlFor="basePrice">Base Price</label>
          <InputNumber 
            id="basePrice" 
            value={formData.basePrice} 
            onValueChange={(e) => setFormData(prev => ({ ...prev, basePrice: e.value ?? 0 }))} 
            mode="currency" 
            currency="USD" 
            required 
          />
        </div>

        <div className="field">
          <label htmlFor="category">Category</label>
          <InputText 
            id="category" 
            value={formData.category} 
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))} 
          />
        </div>

        <div className="field">
          <label htmlFor="manufacturer">Manufacturer</label>
          <InputText 
            id="manufacturer" 
            value={formData.manufacturer} 
            onChange={(e) => setFormData(prev => ({ ...prev, manufacturer: e.target.value }))} 
          />
        </div>

        <div className="field">
          <label htmlFor="imageUrl">Image URL</label>
          <InputText 
            id="imageUrl" 
            value={formData.imageUrl} 
            onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))} 
          />
        </div>

        <div className="field">
          <label htmlFor="quickbooksItemId">QuickBooks Item ID</label>
          <InputText 
            id="quickbooksItemId" 
            value={formData.quickbooksItemId} 
            onChange={(e) => setFormData(prev => ({ ...prev, quickbooksItemId: e.target.value }))} 
          />
        </div>
      </Dialog>
    </div>
  );
};
